'use client'

import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import type { Address } from 'viem'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { E_LEND_CHAIN_ID, EVC_ADDRESS } from '@/config/euler-contracts'
import { getSubAccountAddress } from '../shared/subAccounts'

export interface UseSubaccountControllersParams {
  /** Owner address */
  owner?: Address
  /** List of subaccount IDs to query controllers for */
  subaccountIds: number[]
  /** Chain ID */
  chainId?: number
  /** Whether to enable the query */
  enabled?: boolean
}

export interface UseSubaccountControllersResult {
  /** Map of subaccount ID to their controller addresses */
  subaccountControllers: Map<number, Address[]>
  /** Whether the query is loading */
  isLoading: boolean
  /** Error if any */
  error: Error | null
}

/**
 * Hook to query controllers for a specific set of subaccount IDs.
 *
 * This is more efficient than querying all subaccounts when you already
 * know which ones are active from the discovery cache.
 */
export function useSubaccountControllers({
  owner,
  subaccountIds,
  chainId = E_LEND_CHAIN_ID,
  enabled = true,
}: UseSubaccountControllersParams): UseSubaccountControllersResult {
  // Build multicall contracts for the specified subaccount IDs
  const contracts = useMemo(() => {
    if (!owner || !enabled || subaccountIds.length === 0) return []

    return subaccountIds.map((subAccountId) => ({
      address: EVC_ADDRESS as Address,
      abi: eulerVaultConnectorAbi,
      functionName: 'getControllers' as const,
      args: [getSubAccountAddress(owner, subAccountId)] as const,
      chainId,
    }))
  }, [owner, subaccountIds, chainId, enabled])

  const { data, isLoading, error } = useReadContracts({
    contracts,
    query: {
      enabled: enabled && !!owner && contracts.length > 0,
      staleTime: 30_000,
    },
  })

  // Parse results into a map
  const subaccountControllers = useMemo(() => {
    const map = new Map<number, Address[]>()

    if (!data) return map

    subaccountIds.forEach((subAccountId, index) => {
      const result = data[index]
      if (result?.status === 'success') {
        map.set(subAccountId, result.result as Address[])
      } else {
        map.set(subAccountId, [])
      }
    })

    return map
  }, [data, subaccountIds])

  return {
    subaccountControllers,
    isLoading,
    error: error as Error | null,
  }
}
